"use client";
import React, { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { motion, MotionValue, motionValue, useSpring, useTransform } from "motion/react";
import useMeasure from "react-use-measure";

const TRANSITION = {
  type: "spring",
  stiffness: 280,
  damping: 18,
  mass: 0.3,
};

function Digit({ value, place }: { value: number; place: number }) {
  const valueRoundedToPlace = Math.floor(value / place) % 10;
  const initial = motionValue(valueRoundedToPlace);
  const animatedValue = useSpring(initial, TRANSITION);

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <div className="relative inline-block w-[1ch] overflow-x-visible overflow-y-clip leading-none tabular-nums">
      <div className="invisible">0</div>
      {Array.from({ length: 10 }, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} />
      ))}
    </div>
  );
}

function Number({ mv, number }: { mv: MotionValue<number>; number: number }) {
  const uniqueId = useId();
  const [ref, bounds] = useMeasure();

  const y = useTransform(mv, (latest) => {
    if (!bounds.height) return 0;
    const placeValue = latest % 10;
    const offset = (10 + number - placeValue) % 10;
    let memo = offset * bounds.height;

    if (offset > 5) {
      memo -= 10 * bounds.height;
    }

    return memo;
  });

  // don't render the animated number until we know the height
  if (!bounds.height) {
    return (
      <span ref={ref} className="invisible absolute">
        {number}
      </span>
    );
  }

  return (
    <motion.span
      ref={ref}
      className="absolute inset-0 flex items-center justify-center"
      layoutId={`${uniqueId}-${number}`}
      style={{ y }}
      transition={TRANSITION}
    >
      {number}
    </motion.span>
  );
}

type SlidingNumberProps = {
  value: number;
  padStart?: boolean;
  decimalSeparator?: string;
};

export function SlidingNumber({
  value,
  padStart = false,
  decimalSeparator = ".",
}: SlidingNumberProps) {
  const absValue = Math.abs(value);
  const [integerPart, decimalPart] = absValue.toString().split(".");
  const integerValue = parseInt(integerPart, 10);
  const paddedInteger = padStart && integerValue < 10 ? `0${integerPart}` : integerPart;
  const integerDigits = paddedInteger.split("");
  const integerPlaces = integerDigits.map((_, i) => Math.pow(10, integerDigits.length - i - 1));

  return (
    <div className="flex w-full items-center justify-center">
      {value < 0 && "-"}
      {integerDigits.map((_, index) => (
        <Digit
          key={`pos-${integerPlaces[index]}`}
          place={integerPlaces[index]}
          value={integerValue}
        />
      ))}
      {decimalPart && (
        <>
          <span>{decimalSeparator}</span>
          {decimalPart.split("").map((_, index) => (
            <Digit
              key={`decimal-${index}`}
              place={Math.pow(10, decimalPart.length - index - 1)}
              value={parseInt(decimalPart, 10)}
            />
          ))}
        </>
      )}
    </div>
  );
}

// New ContentEditableSlidingNumber component
type ContentEditableSlidingNumberProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
  disabled?: boolean;
};

export function ContentEditableSlidingNumber({
  value,
  onChange,
  min = 0,
  max = 999,
  className = "",
  disabled = false,
}: ContentEditableSlidingNumberProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const contentRef = useRef<HTMLDivElement>(null);

  const handleFocus = () => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(value.toString());
  };

  const handleBlur = () => {
    setIsEditing(false);
    const newValue = parseInt(editValue, 10) || min;
    const clampedValue = Math.max(min, Math.min(max, newValue));

    onChange(clampedValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      contentRef.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditValue(value.toString());
      contentRef.current?.blur();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const newValue = Math.min(max, value + 1);

      onChange(newValue);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const newValue = Math.max(min, value - 1);

      onChange(newValue);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const text = e.currentTarget.textContent || "";
    // Only allow numbers
    const numericText = text.replace(/[^0-9]/g, "");

    setEditValue(numericText);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    const numericText = pastedText.replace(/[^0-9]/g, "");

    setEditValue(numericText);
  };

  useEffect(() => {
    if (isEditing && contentRef.current) {
      contentRef.current.focus();
      // Select all text when editing starts
      const range = document.createRange();
      const selection = window.getSelection();

      range.selectNodeContents(contentRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  if (isEditing) {
    return (
      <div
        ref={contentRef}
        suppressContentEditableWarning
        className={`outline-none ${className}`}
        contentEditable={"plaintext-only"}
        role={"button"}
        style={{
          minWidth: "1ch",
          display: "inline-block",
          textAlign: "center",
        }}
        onBlur={handleBlur}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      >
        {editValue}
      </div>
    );
  }

  return (
    <div
      aria-label="Credit amount input"
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={value}
      className={`flex cursor-text items-center justify-center select-none ${className} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
      role="spinbutton"
      tabIndex={disabled ? -1 : 0}
      onClick={handleFocus}
      onFocus={handleFocus}
    >
      <SlidingNumber value={value} />
    </div>
  );
}
